/**
 * `withCache(api, userId)` — wrap a `BackendApi` instance so:
 *
 *   1. The hot read procedures route through the SWR cache:
 *        recipe.list                (staleTime 30s)
 *        recipe.get                 (staleTime 30s)
 *        attempt.list               (staleTime 15s)
 *        collection.list            (staleTime 60s)
 *        collection.listAllMembers  (staleTime 60s)  — perf plan §2.C
 *
 *   2. Mutating procedures call `invalidate(...)` on success so the next read
 *      of the affected family fetches fresh data:
 *        recipe.{create, update, delete, promote, spinOff} → invalidate("recipe")
 *        branch.{create, archive, promote, rename, delete} → invalidate("recipe") (variations live under recipes)
 *        attempt.{appendObs}                               → invalidate("attempt")
 *        version.{promote, saveAsVersion, discardDraft}    → invalidate("attempt") + invalidate("recipe")
 *        version.{amendRecord, setLabel}                   → invalidate("attempt")
 *        collection.{create, rename, delete, addMembership, removeMembership} → invalidate("collection")
 *
 * Why the wrapper does both:
 *   - Centralising invalidation here means the dozens of mutation call sites
 *     (Workspace, FlatRecipeList, RecipePage, SpeedLog, …) need zero
 *     changes. Without a wrapper-level guarantee we would need to thread an
 *     invalidate(...) call through every async mutation handler — error-prone
 *     and likely to be missed on the first new feature that lands.
 *   - The wrapper preserves the exact `BackendApi` shape so call sites are
 *     unchanged. Pass-through methods (e.g. ratingSchema.*, batch.*, search.*,
 *     libraryConfig.*, branch.list, branch.get, attempt.get) are inherited via
 *     spread.
 *
 * Branch and recipe share an invalidation family:
 *   The `recipe.list` payload includes `variations` for each recipe, so any
 *   variation-level mutation (branch.create / archive / promote / rename /
 *   delete) must drop the cached recipe.list as well. The simplest correct
 *   policy is: branch mutations invalidate the `recipe` family. (A finer split
 *   could exist, but the over-invalidation cost is one network read per
 *   variation mutation — negligible.)
 *
 * Failure policy:
 *   - The mutation's own rejection bubbles up unchanged so the call site's
 *     Rule-8 toast/inline-error path still works.
 *   - Invalidation runs ONLY on success. Failure to invalidate the cache is
 *     logged + dropped (best-effort) — the user's mutation already succeeded
 *     and stale-by-up-to-30s data is recoverable on the next read or refresh.
 *
 * Defensive partial-mock handling:
 *   Tests sometimes mock the backend with a partial shape (e.g. just
 *   `{ current: {} }`). Spreading `undefined` is a no-op but reading
 *   `api.recipe.list` blows up. We coerce undefined to empty objects on the
 *   sub-namespace level so construction succeeds; the wrapped methods will
 *   still reject at call time (the mock's responsibility) but `withCache`
 *   itself is total.
 *
 * Ref: tasks/pr-body-p4-followup-3.md Path A.
 */

import type { BackendApi } from "../backend/types.js";
import { cacheKey } from "./key.js";
import { invalidate } from "./invalidate.js";
import { getCached } from "./swr.js";

const STALE_TIME = {
  recipeList: 30_000,
  recipeGet: 30_000,
  attemptList: 15_000,
  collectionList: 60_000,
  // Perf plan §2.C — the batched membership read mirrors collection.list's SWR
  // policy (60 s TTL, `collection` mutation-family invalidation).
  collectionAllMembers: 60_000,
} as const;

/** The read procedures that route through the SWR cache. */
export type CachedReadProcedure =
  "recipe.list" | "recipe.get" | "attempt.list" | "collection.list" | "collection.listAllMembers";

/**
 * Optional revalidation hooks for `withCache`.
 *
 * `onUpdate(procedure, data)` fires when a stale-hit background revalidation
 * lands fresh data for one of the cached read procedures (see
 * `SwrParams.onUpdate` in swr.js for the exact semantics — never on fresh
 * hits or cold misses). The UI uses this to re-set state so a stale-served
 * render is corrected once the refresh resolves. `data` is the raw resolved
 * value of the underlying procedure call; the consumer narrows it per
 * procedure.
 */
export interface WithCacheHooks {
  onUpdate?: (procedure: CachedReadProcedure, data: unknown) => void;
  /**
   * `onServe(procedure, data, meta)` fires every time a cached read RESOLVES to
   * a value the caller receives — on a cold miss (`fromCache: false`, live
   * network data), a fresh hit, AND a stale hit (both `fromCache: true`). It
   * fires just before the read's promise settles for the caller.
   *
   * Unlike `onUpdate` (which only fires for a background revalidation landing
   * fresh data), `onServe` lets the consumer learn the SOURCE of the value it
   * is about to apply. The library appliers use `meta.fromCache` to decide
   * whether an empty `recipe.list` payload is authoritative: a cold-miss empty
   * is a genuine "no recipes" from the wire; a cache-sourced empty may be a
   * stale first-run snapshot and must not wipe a populated library. `meta.input`
   * is the raw procedure input so the consumer can scope which reads drive UI
   * state (e.g. only the full library snapshot, not a paginated Palette page).
   */
  onServe?: (
    procedure: CachedReadProcedure,
    data: unknown,
    meta: { fromCache: boolean; input: unknown },
  ) => void;
}

/**
 * Best-effort post-mutation invalidation. Logs on failure; does not throw.
 */
function fireInvalidate(userId: string | null, family: "recipe" | "attempt" | "collection"): void {
  void invalidate(userId, family).catch((err) => {
    console.warn("[proof.cache] invalidate-after-mutation failed", { family, err });
  });
}

/**
 * Post-mutation invalidation the wrapped mutation AWAITS before resolving.
 *
 * Why `version.*` awaits while the other namespaces fire-and-forget: the
 * versions shelf refetches `attempt.list` synchronously on the mutation
 * promise (Workspace bumps `readAttemptsRefreshKey` right after
 * `api.version.promote(...)` resolves). A fire-and-forget purge only clears
 * the L1 tier synchronously; the IndexedDB delete races the follow-up read,
 * which L1-misses and can rehydrate the STALE entry from IDB — the go-to
 * marker then never moves without a reload (dev verification 2026-07-02,
 * BUG-1). Awaiting closes the race deterministically.
 *
 * Failure policy unchanged: invalidation errors are logged and swallowed —
 * the user's mutation already succeeded and stale-by-up-to-15s data is
 * recoverable on the next read or refresh.
 *
 * Exported for the app.tsx `saveAsVersion` shim: the live-WS / raw-fallback
 * freeze path (`handleSaveAsVersion`) bypasses this wrapper's
 * `version.saveAsVersion` arm entirely, so it must call this same helper by hand
 * to drop the attempt + recipe families after the freeze resolves. Reusing this
 * function keeps the await-Promise.all-with-per-family-catch behaviour identical
 * to the wrapped path (rather than duplicating it at the call site).
 */
export async function settleInvalidate(
  userId: string | null,
  families: ReadonlyArray<"recipe" | "attempt" | "collection">,
): Promise<void> {
  await Promise.all(
    families.map((family) =>
      invalidate(userId, family).catch((err) => {
        console.warn("[proof.cache] invalidate-after-mutation failed", { family, err });
      }),
    ),
  );
}

/**
 * Wrap a `BackendApi` so reads hit the SWR cache and mutations invalidate the
 * relevant cache family on success.
 *
 * `userId` may be `null` for the anonymous fallback. Cache keys then use the
 * `__anon__` namespace; the next sign-in's `invalidateAll(...)` call uses the
 * real userId, so anonymous-namespaced entries age out via IDB LRU rather than
 * being explicitly invalidated.
 */
export function withCache(
  api: BackendApi,
  userId: string | null,
  hooks?: WithCacheHooks,
): BackendApi {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const a = (api ?? {}) as any;
  // Per-procedure onUpdate adapters — `undefined` when no hook is registered
  // so the conditional spread below omits the key entirely
  // (exactOptionalPropertyTypes).
  const onUpdateFor = (procedure: CachedReadProcedure): ((data: unknown) => void) | undefined => {
    const onUpdate = hooks?.onUpdate;
    if (!onUpdate) return undefined;
    return (data) => onUpdate(procedure, data);
  };
  // Fire the serve hook (if registered) with the source of the served value.
  // Best-effort: a consumer throw must not surface as a read rejection.
  const fireServe = (
    procedure: CachedReadProcedure,
    data: unknown,
    fromCache: boolean,
    input: unknown,
  ): void => {
    const onServe = hooks?.onServe;
    if (!onServe) return;
    try {
      onServe(procedure, data, { fromCache, input });
    } catch (err) {
      console.warn("[proof.cache] onServe callback failed", { procedure, err });
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recipe = (a.recipe ?? {}) as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const branch = (a.branch ?? {}) as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const attempt = (a.attempt ?? {}) as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const version = (a.version ?? {}) as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const collection = (a.collection ?? {}) as any;

  return {
    ...a,
    recipe: {
      ...recipe,
      list: async (input) => {
        const key = cacheKey({ userId, procedure: "recipe.list", input });
        const onUpdate = onUpdateFor("recipe.list");
        const res = await getCached({
          key,
          fetcher: () => recipe.list(input),
          staleTime: STALE_TIME.recipeList,
          ...(onUpdate ? { onUpdate } : {}),
        });
        fireServe("recipe.list", res.data, res.fromCache, input);
        return res.data;
      },
      get: async (uuid) => {
        const key = cacheKey({ userId, procedure: "recipe.get", input: { uuid } });
        const onUpdate = onUpdateFor("recipe.get");
        const res = await getCached({
          key,
          fetcher: () => recipe.get(uuid),
          staleTime: STALE_TIME.recipeGet,
          ...(onUpdate ? { onUpdate } : {}),
        });
        fireServe("recipe.get", res.data, res.fromCache, { uuid });
        return res.data;
      },
      create: async (input) => {
        const out = await recipe.create(input);
        fireInvalidate(userId, "recipe");
        return out;
      },
      update: async (input) => {
        const out = await recipe.update(input);
        fireInvalidate(userId, "recipe");
        return out;
      },
      delete: async (uuid) => {
        const out = await recipe.delete(uuid);
        fireInvalidate(userId, "recipe");
        // Perf §2.C — the UserDO cascades collection_members on recipe delete, so
        // the batched collection.listAllMembers snapshot now holds orphan rows
        // for the deleted recipe. Drop the collection family too.
        fireInvalidate(userId, "collection");
        return out;
      },
      promote: async (input) => {
        const out = await recipe.promote(input);
        fireInvalidate(userId, "recipe");
        return out;
      },
      spinOff: async (input) => {
        const out = await recipe.spinOff(input);
        fireInvalidate(userId, "recipe");
        return out;
      },
      // A-WP3 — feature-photo change invalidates recipe.list so the library card
      // picks up (or drops) the new face on the next revalidation.
      setFeatureMedia: async (input) => {
        const out = await recipe.setFeatureMedia(input);
        fireInvalidate(userId, "recipe");
        return out;
      },
    },
    branch: {
      ...branch,
      // Branch mutations affect recipe.list (variations live inside the
      // recipe payload) — invalidate the recipe family on every variation
      // touch.
      create: async (input) => {
        const out = await branch.create(input);
        fireInvalidate(userId, "recipe");
        return out;
      },
      archive: async (uuid) => {
        const out = await branch.archive(uuid);
        fireInvalidate(userId, "recipe");
        return out;
      },
      promote: async (input) => {
        const out = await branch.promote(input);
        fireInvalidate(userId, "recipe");
        return out;
      },
      rename: async (branchUuid, newName) => {
        const out = await branch.rename(branchUuid, newName);
        fireInvalidate(userId, "recipe");
        return out;
      },
      delete: async (branchUuid) => {
        const out = await branch.delete(branchUuid);
        fireInvalidate(userId, "recipe");
        return out;
      },
    },
    attempt: {
      ...attempt,
      list: async (input) => {
        const key = cacheKey({ userId, procedure: "attempt.list", input });
        const onUpdate = onUpdateFor("attempt.list");
        const res = await getCached({
          key,
          fetcher: () => attempt.list(input),
          staleTime: STALE_TIME.attemptList,
          ...(onUpdate ? { onUpdate } : {}),
        });
        fireServe("attempt.list", res.data, res.fromCache, input);
        return res.data;
      },
    },
    version: {
      ...version,
      // Version lifecycle mutations rewrite attempt rows (statuses, new frozen
      // versions, deleted drafts), so every one of them must drop the cached
      // `attempt.list` — the versions shelf and cook strip read it (BUG-1,
      // dev verification 2026-07-02). promote / saveAsVersion / discardDraft
      // also move recipe-level pointers (branches.promoted_attempt_uuid,
      // recipes.default_variation_uuid, branch HEAD), so they drop the
      // `recipe` family too. amendRecord only appends outcome annotations to
      // one frozen attempt body — `attempt` alone covers it.
      promote: async (attemptUuid) => {
        const out = await version.promote(attemptUuid);
        await settleInvalidate(userId, ["attempt", "recipe"]);
        return out;
      },
      saveAsVersion: async (input) => {
        const out = await version.saveAsVersion(input);
        await settleInvalidate(userId, ["attempt", "recipe"]);
        return out;
      },
      discardDraft: async (input) => {
        const out = await version.discardDraft(input);
        await settleInvalidate(userId, ["attempt", "recipe"]);
        return out;
      },
      amendRecord: async (input) => {
        const out = await version.amendRecord(input);
        await settleInvalidate(userId, ["attempt"]);
        return out;
      },
      // A-WP8 — setLabel rewrites the frozen attempt's `version_label` column,
      // which the versions shelf row AND the peek FrozenBanner read straight off
      // `attempt.list`. Without dropping the cached list the post-mutation
      // refetch (Workspace bumps `readAttemptsRefreshKey` on success) re-serves
      // the stale <15s label-less snapshot, so the just-named label never
      // renders on the peek banner or the row (dev e2e verification 2026-07-11,
      // named-versions.spec.ts:103). Attempt-only, like amendRecord: the label
      // is navigation metadata on the attempt row, not on the recipe card, so
      // `recipe.list` is untouched. (It also joins FTS server-side, but the
      // search RPC is not cached here.)
      setLabel: async (input) => {
        const out = await version.setLabel(input);
        await settleInvalidate(userId, ["attempt"]);
        return out;
      },
    },
    collection: {
      ...collection,
      list: async () => {
        const key = cacheKey({ userId, procedure: "collection.list", input: {} });
        const onUpdate = onUpdateFor("collection.list");
        const res = await getCached({
          key,
          fetcher: () => collection.list(),
          staleTime: STALE_TIME.collectionList,
          ...(onUpdate ? { onUpdate } : {}),
        });
        fireServe("collection.list", res.data, res.fromCache, {});
        return res.data;
      },
      // Perf plan §2.C — batched membership read. Same SWR seam as
      // collection.list: 60 s TTL, prefix-invalidated by the `collection`
      // family (every membership mutation below drops it), optional onUpdate so
      // a stale-served membership map self-corrects on background revalidation.
      listAllMembers: async () => {
        const key = cacheKey({ userId, procedure: "collection.listAllMembers", input: {} });
        const onUpdate = onUpdateFor("collection.listAllMembers");
        const res = await getCached({
          key,
          fetcher: () => collection.listAllMembers(),
          staleTime: STALE_TIME.collectionAllMembers,
          ...(onUpdate ? { onUpdate } : {}),
        });
        fireServe("collection.listAllMembers", res.data, res.fromCache, {});
        return res.data;
      },
      create: async (name) => {
        const out = await collection.create(name);
        fireInvalidate(userId, "collection");
        return out;
      },
      rename: async (uuid, name) => {
        const out = await collection.rename(uuid, name);
        fireInvalidate(userId, "collection");
        return out;
      },
      delete: async (uuid) => {
        const out = await collection.delete(uuid);
        fireInvalidate(userId, "collection");
        return out;
      },
      addMembership: async (collectionUuid, recipeUuid) => {
        const out = await collection.addMembership(collectionUuid, recipeUuid);
        fireInvalidate(userId, "collection");
        return out;
      },
      removeMembership: async (collectionUuid, recipeUuid) => {
        const out = await collection.removeMembership(collectionUuid, recipeUuid);
        fireInvalidate(userId, "collection");
        return out;
      },
    },
  };
}
